/**
 * Flex monthly billing — pure helpers shared across the parent + admin portals.
 *
 * Flex enrollment bills monthly for the *confirmed dates* in that month's plan
 * (`flex_month_plans.dates`), priced at the program's per-day tuition rate. Both the
 * parent and the admin co-edit and preview the *next* month's plan until a global lock
 * day (the 15th of the prior month), after which the plan freezes.
 *
 * Everything here is pure and clock-injectable so lock-date and billing math can be unit
 * tested with a pinned clock (no `new Date()` date-bombs). The same rules are mirrored
 * server-side in supabase/functions/_shared/flex.ts and in the save RPC / generator.
 */
import settings from '../config/settings.json';
import { getTodayDateStringInBusinessTZ } from './date';
import { NON_PRORATED_SUBTYPES } from './billingDisplay';

export type FlexPlanStatus = 'draft' | 'locked' | 'billed' | 'skipped';

export interface FlexConfig {
  /** UI/seeding guidance only — not server-enforced. */
  minDaysPerWeek: number;
  /** UI/seeding guidance only (Mon–Fri). */
  maxDaysPerWeek: number;
  /** Day of month after which the *next* month's plan locks. Server-enforced. */
  lockDay: number;
  /** How many future months beyond the coming month the plan-ahead UI opens (UI-only). */
  planAheadMonths: number;
}

export function getFlexConfig(): FlexConfig {
  const f = (settings as { flex?: Partial<FlexConfig> }).flex ?? {};
  return {
    minDaysPerWeek: f.minDaysPerWeek ?? 1,
    maxDaysPerWeek: f.maxDaysPerWeek ?? 5,
    lockDay: f.lockDay ?? 15,
    planAheadMonths: f.planAheadMonths ?? 0,
  };
}

/** First-of-month (YYYY-MM-01) for the month containing a YYYY-MM-DD date string. */
export function monthStartOf(dateYmd: string): string {
  return `${dateYmd.slice(0, 7)}-01`;
}

/** Add `n` months to a YYYY-MM-01 string, returning the new YYYY-MM-01 string. */
export function addMonths(monthStartYmd: string, n: number): string {
  const [y, m] = monthStartYmd.split('-').map(Number);
  const zeroIdx = (y * 12 + (m - 1)) + n;
  const ny = Math.floor(zeroIdx / 12);
  const nm = (zeroIdx % 12) + 1;
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}-01`;
}

/** Number of calendar days in the month containing `monthStartYmd` (a YYYY-MM-01). */
export function daysInMonth(monthStartYmd: string): number {
  const [y, m] = monthStartYmd.split('-').map(Number);
  // Day 0 of next month === last day of this month.
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * True when a plan for `planMonth` (YYYY-MM-01) is locked as of `todayYmd`.
 *
 * The editing window for month M+1 is open through `lockDay` of month M; on the day
 * *after* the lock day it freezes. We never gate on time-of-day — comparing date
 * strings in the business TZ is sufficient and avoids `new Date()` flakiness.
 */
export function isPlanLocked(
  planMonthYmd: string,
  todayYmd: string = getTodayDateStringInBusinessTZ(),
  lockDay: number = getFlexConfig().lockDay,
): boolean {
  // The lock cutoff is `lockDay` of the month *before* planMonth.
  const priorMonth = addMonths(monthStartOf(planMonthYmd), -1);
  const lockDateYmd = `${priorMonth.slice(0, 7)}-${String(lockDay).padStart(2, '0')}`;
  return todayYmd > lockDateYmd;
}

/**
 * The next month a parent/admin can still edit, as of `todayYmd`. While today is on/before
 * the lock day of the current month, that's next month (M+1); once past the lock day, M+1
 * has frozen so the next editable month is M+2.
 */
export function nextEditableMonth(
  todayYmd: string = getTodayDateStringInBusinessTZ(),
  lockDay: number = getFlexConfig().lockDay,
): string {
  const day = Number(todayYmd.slice(8, 10));
  return addMonths(monthStartOf(todayYmd), day <= lockDay ? 1 : 2);
}

/**
 * Default target month for the admin "Prepare next-month plans" seeding op: the next
 * calendar month. Matches the server default in flex_seed_next_month_plans().
 */
export function defaultSeedMonth(
  todayYmd: string = getTodayDateStringInBusinessTZ(),
): string {
  return addMonths(monthStartOf(todayYmd), 1);
}

/**
 * Months the plan surface may open as of `todayYmd`: the first editable (coming/billing)
 * month at index 0, plus up to `planAhead` future months for plan-ahead convenience,
 * stopping at `programEndMonth` (any YYYY-MM-DD in the program's final month) when provided.
 *
 * The lower bound is clamped to the enrollment start month (`enrollmentStartYmd`) so a parent
 * can't plan *before* their enrollment begins: a program running Aug–Dec with a September
 * start opens Sep onward, never August. The per-month lock still applies independently; the
 * UI additionally gates each later month on the prior one already being saved.
 */
export function editablePlanMonths(
  todayYmd: string = getTodayDateStringInBusinessTZ(),
  planAhead: number = getFlexConfig().planAheadMonths,
  programEndMonthYmd?: string | null,
  enrollmentStartYmd?: string | null,
): string[] {
  const rollingBase = nextEditableMonth(todayYmd);
  const startMonth = enrollmentStartYmd ? monthStartOf(enrollmentStartYmd) : null;
  // Never open a month before the enrollment starts.
  const base = startMonth && startMonth > rollingBase ? startMonth : rollingBase;
  const endMonth = programEndMonthYmd ? monthStartOf(programEndMonthYmd) : null;
  const out: string[] = [];
  for (let i = 0; i <= Math.max(0, planAhead); i++) {
    const mo = addMonths(base, i);
    if (endMonth && mo > endMonth) break;
    out.push(mo);
  }
  return out;
}

/**
 * Months worth showing in a *read-only* plan summary (admin enrollment detail): the current
 * month — what the student is attending right now — through the next editable month, so an
 * admin sees both "this month" and "what's booked next" in one glance. Unlike
 * `editablePlanMonths` this looks backward to today rather than forward from the lock, and
 * is never gated on the enrollment start. Bounded at 3 entries.
 */
export function flexSummaryMonths(
  todayYmd: string = getTodayDateStringInBusinessTZ(),
): string[] {
  const last = nextEditableMonth(todayYmd);
  const out: string[] = [];
  for (let m = monthStartOf(todayYmd); m <= last; m = addMonths(m, 1)) {
    out.push(m);
  }
  return out;
}

/**
 * Who last touched a plan, in a form worth reading. `flex_month_plans.updated_by` is
 * stamped as `parent:<uuid>` | `admin:<email>` | `system`. The parent UUID means nothing
 * to an admin and, as one unbreakable 36-character token, is the widest thing in the plan
 * card — so it is dropped. An admin email identifies a real person, so it is kept.
 */
export function formatUpdatedBy(updatedBy: string): string {
  if (updatedBy.startsWith('parent:')) return 'parent';
  if (updatedBy.startsWith('admin:')) return updatedBy.slice('admin:'.length) || 'admin';
  return updatedBy;
}

export interface FlexPlanMonthLike {
  /** First-of-month (or any YYYY-MM-DD in that month) the plan covers. */
  planMonth: string;
  dates: readonly string[];
}

/**
 * Infer a family's *regular* weekday cadence from recent confirmed plans, robust to one-off
 * skips — a single holiday-dropped Monday shouldn't drop Monday from the pattern. For each
 * weekday we take selected-occurrences ÷ total-occurrences across the source months and keep
 * the weekday when it was chosen in at least `threshold` (default 0.5) of its occurrences.
 * Returns weekdays (0=Sun…6=Sat) ascending; empty source → [].
 */
export function deriveWeekdayPattern(
  months: readonly FlexPlanMonthLike[],
  threshold = 0.5,
): number[] {
  const selected = new Array(7).fill(0) as number[];
  const total = new Array(7).fill(0) as number[];
  for (const mo of months) {
    const monthStart = monthStartOf(mo.planMonth);
    const prefix = monthStart.slice(0, 7);
    const [y, m] = monthStart.split('-').map(Number);
    const dim = daysInMonth(monthStart);
    for (let day = 1; day <= dim; day++) {
      total[new Date(Date.UTC(y, m - 1, day)).getUTCDay()] += 1;
    }
    for (const d of new Set(mo.dates)) {
      if ((d ?? '').slice(0, 7) !== prefix) continue;
      const [dy, dm, dd] = d.split('-').map(Number);
      selected[new Date(Date.UTC(dy, dm - 1, dd)).getUTCDay()] += 1;
    }
  }
  const out: number[] = [];
  for (let dow = 0; dow < 7; dow++) {
    if (total[dow] > 0 && selected[dow] / total[dow] >= threshold) out.push(dow);
  }
  return out;
}

export interface FlexSuggestion {
  dates: string[];
  weekdays: number[];
  /** Dates dropped because they landed on a known school holiday. */
  removedHolidays: string[];
  /** Which input produced the cadence: recent plans, the fallback cadence, or nothing. */
  source: 'history' | 'fallback' | 'none';
}

/**
 * Suggested prefill for a plan-ahead month: infer the regular weekday cadence from recent
 * confirmed plans (falling back to a provided default cadence — e.g. the program schedule),
 * expand across `targetMonth`, then drop any dates landing on a known school holiday. Pure;
 * the caller passes the holiday set (school_holidays coverage is spotty, so the "removed
 * holidays" list is often empty — the caller decides whether to surface it).
 */
export function suggestPlanDates(
  targetMonthYmd: string,
  opts: {
    sourceMonths?: readonly FlexPlanMonthLike[];
    fallbackWeekdays?: readonly number[];
    holidays?: ReadonlySet<string>;
  } = {},
): FlexSuggestion {
  const fromHistory = deriveWeekdayPattern(opts.sourceMonths ?? []);
  let weekdays: number[];
  let source: FlexSuggestion['source'];
  if (fromHistory.length > 0) {
    weekdays = fromHistory;
    source = 'history';
  } else if ((opts.fallbackWeekdays ?? []).length > 0) {
    weekdays = [...new Set(opts.fallbackWeekdays)].sort((a, b) => a - b);
    source = 'fallback';
  } else {
    weekdays = [];
    source = 'none';
  }

  const holidays = opts.holidays ?? new Set<string>();
  const removedHolidays: string[] = [];
  const dates = seedDatesFromWeekdays(monthStartOf(targetMonthYmd), weekdays).filter((d) => {
    if (holidays.has(d)) {
      removedHolidays.push(d);
      return false;
    }
    return true;
  });
  return { dates, weekdays, removedHolidays, source };
}

export interface PlanValidationResult {
  valid: boolean;
  /** Normalized: in-month, deduped, sorted ascending. */
  dates: string[];
  errors: string[];
}

/**
 * Validate + normalize a set of confirmed dates against a plan month.
 *
 * Rules (MVP): every date must be a real YYYY-MM-DD that falls inside `planMonth`;
 * duplicates are collapsed. **0 dates is valid** (an intentional pause). There is no
 * hard monthly min/max — per-week numbers are UI guidance only.
 */
export function validatePlanDates(
  planMonthYmd: string,
  dates: readonly string[],
): PlanValidationResult {
  const errors: string[] = [];
  const monthPrefix = planMonthYmd.slice(0, 7); // YYYY-MM
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const raw of dates) {
    const d = (raw ?? '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      errors.push(`Invalid date: ${raw}`);
      continue;
    }
    if (d.slice(0, 7) !== monthPrefix) {
      errors.push(`Date ${d} is outside ${monthPrefix}`);
      continue;
    }
    // Guard against e.g. 2026-02-31 — reconstruct and compare.
    const [y, m, day] = d.split('-').map(Number);
    const reconstructed = new Date(Date.UTC(y, m - 1, day));
    if (
      reconstructed.getUTCFullYear() !== y ||
      reconstructed.getUTCMonth() !== m - 1 ||
      reconstructed.getUTCDate() !== day
    ) {
      errors.push(`Invalid calendar date: ${d}`);
      continue;
    }
    if (seen.has(d)) continue;
    seen.add(d);
    normalized.push(d);
  }

  normalized.sort();
  return { valid: errors.length === 0, dates: normalized, errors };
}

/**
 * Candidate seed dates for a plan month from a weekly weekday template
 * (0=Sun … 6=Sat). Used to pre-fill a new month's draft; not billed itself.
 */
export function seedDatesFromWeekdays(
  planMonthYmd: string,
  weekdays: readonly number[],
): string[] {
  const wanted = new Set(weekdays);
  if (wanted.size === 0) return [];
  const [y, m] = planMonthYmd.split('-').map(Number);
  const total = daysInMonth(planMonthYmd);
  const out: string[] = [];
  for (let day = 1; day <= total; day++) {
    const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
    if (wanted.has(dow)) {
      out.push(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    }
  }
  return out;
}

/**
 * Count the dates that fall on/after a mid-month enrollment start. The generator only
 * bills dates on/after `enrollment_start_date`; earlier dates in the seeded plan are
 * ignored (a student enrolling on the 10th isn't billed for the 3rd).
 */
export function countBillableDates(
  dates: readonly string[],
  enrollmentStartYmd: string | null,
): number {
  if (!enrollmentStartYmd) return dates.length;
  return dates.filter((d) => d >= enrollmentStartYmd).length;
}

/**
 * The day-of-month numbers of a plan's confirmed dates, ascending and comma-joined —
 * "8, 10, 15, 17". Used where the month is already named alongside (the past-months list),
 * so repeating it per date would be noise. Dates outside `planMonth` are dropped, and
 * duplicates collapsed; an empty plan returns ''. Pure string math — no `new Date()`, so no
 * timezone shifts a date onto the wrong day.
 */
export function formatPlanDayNumbers(
  dates: readonly string[] | null | undefined,
  planMonthYmd: string,
): string {
  const prefix = planMonthYmd.slice(0, 7);
  const days = [...new Set(dates ?? [])]
    .filter((d) => (d ?? '').slice(0, 7) === prefix)
    .sort()
    .map((d) => Number(d.slice(8, 10)))
    .filter((n) => Number.isFinite(n) && n > 0);
  return days.join(', ');
}

export interface FlexPricingRow {
  /** line_item_config id — used to honor the family's add-on choice. */
  id?: string;
  amount: number;
  type?: string | null;
  subtype?: string | null;
  /** `opt_in_items` only speaks for optional rows, so the predicate needs this. */
  isOptional?: boolean;
}

/**
 * Whether a flex line item is **day-counted** (billed × attended days) vs. flat. Mirrors
 * the recurring proration rule: materials are one-time (skipped in the monthly job), and
 * the flat-by-nature subtypes (`Deposit`, `late_fee`, `processing`, `coupon`,
 * `non_prorated`) bill their full amount; everything else (tuition + per-day fees like
 * transportation) is day-counted. Uses the same set as the prorated-badge logic.
 */
export function isFlexDayCounted(type?: string | null, subtype?: string | null): boolean {
  if (type === 'materials') return false;
  if (subtype && NON_PRORATED_SUBTYPES.has(subtype)) return false;
  return true;
}

/**
 * Split a program's pricing into the flex per-day rate vs. flat add-ons, honoring opt-outs.
 * Day-counted items (tuition + per-day fees) go to `perDayRate`; flat-by-nature subtypes go
 * to `monthlyAddOns`. Materials are one-time: excluded by default (later months), but
 * included as a flat add-on when `includeMaterials` is set (the first-month estimate, which
 * bills materials once).
 *
 * Add-ons follow `optIn` — the family's positive selection — which is what billing reads, so
 * the quote and the invoice cannot drift. An id absent from `optIn` is simply not charged.
 */
function billsRow(r: FlexPricingRow, optIn: Set<string>): boolean {
  // Optional rows bill only when chosen; everything else is not declinable.
  if (!r.isOptional) return true;
  return optIn.has(r.id as string);
}

export function splitFlexPricing(
  rows: readonly FlexPricingRow[],
  opts: { includeMaterials?: boolean; optIn?: readonly string[] | null } = {},
): { perDayRate: number; monthlyAddOns: number } {
  const optIn = new Set(opts.optIn ?? []);
  let perDayRate = 0;
  let monthlyAddOns = 0;
  for (const r of rows) {
    if (r.id && !billsRow(r, optIn)) continue;
    const amt = Number(r.amount) || 0;
    if (r.type === 'materials') {
      if (opts.includeMaterials) monthlyAddOns += amt;
      continue;
    }
    if (isFlexDayCounted(r.type, r.subtype)) perDayRate += amt;
    else monthlyAddOns += amt;
  }
  return { perDayRate, monthlyAddOns };
}

/**
 * Projected flex cost for a month: billable day count × per-day rate, plus any
 * non-tuition monthly add-ons (passed pre-summed by the caller). Floored at $0.
 * Discounts are applied by the caller/generator — this is the live UI estimate.
 */
export function computeFlexEstimate(
  billableDays: number,
  perDayRate: number,
  monthlyAddOns = 0,
): number {
  const tuition = Math.round(perDayRate * billableDays * 100) / 100;
  return Math.max(0, Math.round((tuition + monthlyAddOns) * 100) / 100);
}
